import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { SessionExpiredError } from "../../shared/api/client.js";
import { useFilterState, type FilterStateController } from "./useFilterState.js";

const FilterStateContext = createContext<FilterStateController | null>(null);

export function FilterStateProvider({
  children,
  onSessionExpired,
}: {
  readonly children: ReactNode;
  readonly onSessionExpired: () => void;
}) {
  const navigate = useNavigate();
  const state = useFilterState({ navigate: (href) => navigate(href) });
  const loadError = state.status === "blocked" ? state.error : undefined;

  useEffect(() => {
    if (loadError instanceof SessionExpiredError) onSessionExpired();
  }, [loadError, onSessionExpired]);

  return (
    <FilterStateContext.Provider value={state}>
      {children}
    </FilterStateContext.Provider>
  );
}

export function usePersistentFilterState(): FilterStateController {
  const state = useContext(FilterStateContext);
  if (state === null) {
    throw new Error("Persistent filter state must be used inside FilterStateProvider.");
  }
  return state;
}
